import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Volume2,
  Search,
  BookOpen,
  FileText,
  Loader2,
  Trash2,
  RotateCcw,
  ShieldAlert,
  ChevronDown,
  Sparkles,
  X,
  Edit3,
  Check,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  getVocabularyUnit,
  getLatestRecordForExam,
  deleteVocabularyUnit,
  deleteVocabularyItems,
  updateExamTitle,
} from "../../../services/examService";
import {
  createVocabDeleteSession,
  verifyExamPassword,
  createExamGenSession,
  verifyExamGenToken,
} from "../../../services/settingsService";
import { resolveInflections, extractBaseWord } from "../../../utils/spanishInflections";
import styles from "./VocabularyPage.module.css";

// IP-bound session token cache for vocab deletion (5-minute TTL set by server)
const SESSION_KEY = "vocabDeleteUnlocked";

// Returns the cached delete session token if it hasn't expired, otherwise null.
function getDeleteSessionToken() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const { expiresAt, token } = JSON.parse(raw);
    if (Date.now() < expiresAt) return token;
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  } catch {
    return null;
  }
}

// Cache the IP-bound token returned by the server.
function cacheDeleteSession(token, expiresInMs) {
  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ expiresAt: Date.now() + expiresInMs, token })
  );
}

/* ── Dynamic Inflections Drawer Component ─────────────────────── */
function InflectionDrawer({ item }) {
  const inflectionData = resolveInflections(item);
  if (!inflectionData) return null;

  const { type, base, notes, conjugations, forms, pronouns } = inflectionData;
  const isVerb = type === "verb";
  const isNoun = type === "noun";
  const isAdj = type === "adjective";

  return (
    <div className={styles.inflectionDrawer}>
      <div className={styles.drawerHeader}>
        <div className={styles.drawerTitle}>
          <Sparkles size={14} className="text-green-600" />
          <span>词形与时态变形 (Inflections & Conjugations): <strong>{base}</strong></span>
        </div>
        <span className={styles.drawerPosTag}>{type}</span>
      </div>

      {/* ── VERB CONJUGATIONS ── */}
      {isVerb && conjugations && (
        <>
          <div className={styles.tenseGrid}>
            {/* Presente */}
            {conjugations.present && (
              <div className={styles.tenseCard}>
                <div className={styles.tenseTitle}>⚡ Presente (现在时)</div>
                <div className={styles.tenseList}>
                  {pronouns.map((pro, i) => (
                    <div key={i} className={styles.conjugationRow}>
                      <span className={styles.pronounLabel}>{pro}</span>
                      <span className={styles.conjugatedForm}>{conjugations.present[i] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pretérito Indefinido */}
            {conjugations.preterite && (
              <div className={styles.tenseCard}>
                <div className={styles.tenseTitle}>⏱️ Pretérito Indefinido (简单过去时)</div>
                <div className={styles.tenseList}>
                  {pronouns.map((pro, i) => (
                    <div key={i} className={styles.conjugationRow}>
                      <span className={styles.pronounLabel}>{pro}</span>
                      <span className={styles.conjugatedForm}>{conjugations.preterite[i] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pretérito Imperfecto */}
            {conjugations.imperfect && (
              <div className={styles.tenseCard}>
                <div className={styles.tenseTitle}>⏳ Imperfecto (过去未完成时)</div>
                <div className={styles.tenseList}>
                  {pronouns.map((pro, i) => (
                    <div key={i} className={styles.conjugationRow}>
                      <span className={styles.pronounLabel}>{pro}</span>
                      <span className={styles.conjugatedForm}>{conjugations.imperfect[i] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pretérito Perfecto (现在完成时) */}
            {conjugations.perfect && (
              <div className={styles.tenseCard}>
                <div className={styles.tenseTitle}>✨ Pretérito Perfecto (现在完成时)</div>
                <div className={styles.tenseList}>
                  {pronouns.map((pro, i) => (
                    <div key={i} className={styles.conjugationRow}>
                      <span className={styles.pronounLabel}>{pro}</span>
                      <span className={styles.conjugatedForm}>{conjugations.perfect[i] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Futuro */}
            {conjugations.future && (
              <div className={styles.tenseCard}>
                <div className={styles.tenseTitle}>🚀 Futuro Simple (将来时)</div>
                <div className={styles.tenseList}>
                  {pronouns.map((pro, i) => (
                    <div key={i} className={styles.conjugationRow}>
                      <span className={styles.pronounLabel}>{pro}</span>
                      <span className={styles.conjugatedForm}>{conjugations.future[i] || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Gerund & Participle */}
          {(conjugations.gerund || conjugations.participle) && (
            <div className={styles.nonFiniteRow}>
              {conjugations.gerund && (
                <div className={styles.nonFiniteItem}>
                  <span className={styles.nonFiniteLabel}>Gerundio (副动词):</span>
                  <span className={styles.nonFiniteVal}>{conjugations.gerund}</span>
                </div>
              )}
              {conjugations.participle && (
                <div className={styles.nonFiniteItem}>
                  <span className={styles.nonFiniteLabel}>Participio (过去分词):</span>
                  <span className={styles.nonFiniteVal}>{conjugations.participle}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── NOUN FORMS ── */}
      {isNoun && forms && (
        <div className={styles.formCardsGrid}>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>单数 (Singular)</span>
            <span className={styles.formCardValue}>{forms.singular}</span>
          </div>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>复数 (Plural)</span>
            <span className={styles.formCardValue}>{forms.plural}</span>
          </div>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>词性与性数 (Gender)</span>
            <span className={styles.formCardValue}>{forms.gender}</span>
          </div>
        </div>
      )}

      {/* ── ADJECTIVE FORMS ── */}
      {isAdj && forms && (
        <div className={styles.formCardsGrid}>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>阳性单数 (Masc. Sing.)</span>
            <span className={styles.formCardValue}>{forms.masculineSingular}</span>
          </div>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>阴性单数 (Fem. Sing.)</span>
            <span className={styles.formCardValue}>{forms.feminineSingular}</span>
          </div>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>阳性复数 (Masc. Plur.)</span>
            <span className={styles.formCardValue}>{forms.masculinePlural}</span>
          </div>
          <div className={styles.formCard}>
            <span className={styles.formCardLabel}>阴性复数 (Fem. Plur.)</span>
            <span className={styles.formCardValue}>{forms.femininePlural}</span>
          </div>
          {forms.adverb && (
            <div className={styles.formCard}>
              <span className={styles.formCardLabel}>副词形式 (Adverb)</span>
              <span className={styles.formCardValue}>{forms.adverb}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Custom notes or phrase breakdown ── */}
      {notes && (
        <div className={styles.notesBox}>
          <strong>语法/变形说明:</strong> {notes}
        </div>
      )}
    </div>
  );
}

export default function VocabularyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [latestRecord, setLatestRecord] = useState(null);

  // Toggle map for expanded inflection drawers: { [spanishKey]: boolean }
  const [expandedKeys, setExpandedKeys] = useState({});

  // Delete target state: null | { type: "unit" } | { type: "item", item: object, index: number }
  const [pendingAction, setPendingAction] = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [showPwdEye, setShowPwdEye] = useState(false);
  const [pwdInput, setPwdInput] = useState("");
  const [pwdError, setPwdError] = useState("");
  const [pwdVerifying, setPwdVerifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDriftingAway, setIsDriftingAway] = useState(false);
  const pwdRef = useRef(null);

  // Exam access password modal state
  const [showExamPwdModal, setShowExamPwdModal] = useState(false);
  const [examPwdInput, setExamPwdInput] = useState("");
  const [examPwdError, setExamPwdError] = useState("");
  const [examPwdVerifying, setExamPwdVerifying] = useState(false);
  const examPwdRef = useRef(null);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [titleSaveError, setTitleSaveError] = useState("");
  const titleInputRef = useRef(null);

  // Title auth (reuses exam-gen session token, 5-min IP-bound)
  const [titleAuthToken, setTitleAuthToken] = useState(() => {
    const stored = sessionStorage.getItem("titleEditToken");
    const expiry = sessionStorage.getItem("titleEditTokenExpiry");
    if (stored && expiry && Date.now() < Number(expiry)) return stored;
    return null;
  });
  const [showTitleAuthModal, setShowTitleAuthModal] = useState(false);
  const [showTitleAuthEye, setShowTitleAuthEye] = useState(false);
  const [titleAuthPassword, setTitleAuthPassword] = useState("");
  const [titleAuthError, setTitleAuthError] = useState("");
  const [titleAuthVerifying, setTitleAuthVerifying] = useState(false);
  const titleAuthInputRef = useRef(null);

  // Pending item deletes: Set of spanish word strings
  const [pendingItemDeletes, setPendingItemDeletes] = useState(new Set());
  const pendingDeletesRef = useRef(new Set());
  const sessionTokenRef = useRef(getDeleteSessionToken());

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      const [data, record] = await Promise.all([
        getVocabularyUnit(id),
        getLatestRecordForExam(id),
      ]);
      if (isMounted) {
        setUnit(data);
        setLatestRecord(record);
        setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Flush pending deletions on page refresh (beforeunload / pagehide) or component unmount (leaving page)
  const flushPendingDeletes = useCallback((keepalive = false) => {
    const toDelete = Array.from(pendingDeletesRef.current);
    if (toDelete.length === 0) return;
    const token = sessionTokenRef.current || getDeleteSessionToken();
    if (!token) return;
    deleteVocabularyItems(id, toDelete, token, keepalive);
    pendingDeletesRef.current.clear();
  }, [id]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flushPendingDeletes(true);
    };
    const handlePageHide = () => {
      flushPendingDeletes(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      flushPendingDeletes(true);
    };
  }, [flushPendingDeletes]);

  // Focus password input when modal opens
  useEffect(() => {
    if (showPwdModal && pwdRef.current) pwdRef.current.focus();
  }, [showPwdModal]);

  useEffect(() => {
    if (showExamPwdModal && examPwdRef.current) examPwdRef.current.focus();
  }, [showExamPwdModal]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (showTitleAuthModal && titleAuthInputRef.current) {
      titleAuthInputRef.current.focus();
    }
  }, [showTitleAuthModal]);

  // Handle Take Exam click — always require password on each click
  const handleTakeExamClick = () => {
    setExamPwdInput("");
    setExamPwdError("");
    setShowExamPwdModal(true);
  };

  const handleExamPwdSubmit = async () => {
    if (examPwdInput.length !== 6) return;
    setExamPwdVerifying(true);
    const valid = await verifyExamPassword(examPwdInput);
    setExamPwdVerifying(false);
    if (!valid) {
      setExamPwdError("Incorrect password. Please try again.");
      return;
    }
    setShowExamPwdModal(false);
    if (latestRecord) {
      navigate(`/study/spanish/exam/${unit.id}?recordId=${latestRecord.id}`);
    } else {
      navigate(`/study/spanish/exam/${unit.id}`);
    }
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const speak = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const { base } = extractBaseWord(text);
    const utterance = new SpeechSynthesisUtterance(base);
    utterance.lang = "es-ES";
    window.speechSynthesis.speak(utterance);
  };

  const cleanTitle = (raw) => {
    if (!raw) return "Unit Vocabulary List";
    return decodeURIComponent(raw.replace(/\+/g, " "));
  };

  const handleStartEditTitle = () => {
    if (!unit) return;
    setEditedTitle(cleanTitle(unit.title) || "");
    setTitleSaveError("");
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setTitleSaveError("");
    setEditedTitle(unit ? cleanTitle(unit.title) : "");
  };

  const doSaveTitle = async (token) => {
    if (!unit) return;
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setTitleSaveError("Title cannot be empty");
      return;
    }

    const targetUnitId = unit.id || id;
    setIsSavingTitle(true);
    setTitleSaveError("");

    try {
      await updateExamTitle(targetUnitId, trimmedTitle, token);
      setUnit((prev) => (prev ? { ...prev, title: trimmedTitle } : prev));

      try {
        const stored = sessionStorage.getItem(`exam-${targetUnitId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.title = trimmedTitle;
          sessionStorage.setItem(`exam-${targetUnitId}`, JSON.stringify(parsed));
        }
      } catch (e) {
        console.error(e);
      }

      try {
        const subject = unit.subject || "spanish";
        const cacheKey = `exam-history-${subject}`;
        const cachedHistoryStr = localStorage.getItem(cacheKey);
        if (cachedHistoryStr) {
          const cachedHistory = JSON.parse(cachedHistoryStr);
          const updatedHistory = cachedHistory.map((item) => {
            if (item.examId === targetUnitId || (item.exam && item.exam.id === targetUnitId)) {
              return { ...item, exam: { ...item.exam, title: trimmedTitle } };
            }
            return item;
          });
          localStorage.setItem(cacheKey, JSON.stringify(updatedHistory));
        }
      } catch (e) {
        console.error(e);
      }

      setIsEditingTitle(false);
    } catch (err) {
      console.error(err);
      const serverMsg = err?.response?.data?.error;
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setTitleAuthToken(null);
        sessionStorage.removeItem("titleEditToken");
        sessionStorage.removeItem("titleEditTokenExpiry");
        setTitleSaveError("Session expired. Please enter the password again.");
        setShowTitleAuthModal(true);
      } else {
        setTitleSaveError(serverMsg || "Failed to update title");
      }
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!unit) return;
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setTitleSaveError("Title cannot be empty");
      return;
    }
    if (trimmedTitle === cleanTitle(unit.title)) {
      setIsEditingTitle(false);
      return;
    }

    const cachedToken = titleAuthToken;
    const cachedExpiry = sessionStorage.getItem("titleEditTokenExpiry");
    const tokenValid = cachedToken && cachedExpiry && Date.now() < Number(cachedExpiry);

    if (tokenValid) {
      const stillValid = await verifyExamGenToken(cachedToken).catch(() => false);
      if (stillValid) {
        await doSaveTitle(cachedToken);
        return;
      }
      setTitleAuthToken(null);
      sessionStorage.removeItem("titleEditToken");
      sessionStorage.removeItem("titleEditTokenExpiry");
    }

    setTitleAuthPassword("");
    setTitleAuthError("");
    setShowTitleAuthEye(false);
    setShowTitleAuthModal(true);
  };

  const handleTitleAuthSubmit = async () => {
    if (titleAuthPassword.length !== 6) {
      setTitleAuthError("Please enter the 6-digit password.");
      return;
    }
    setTitleAuthVerifying(true);
    setTitleAuthError("");
    try {
      const result = await createExamGenSession(titleAuthPassword.trim());
      if (!result.valid) {
        setTitleAuthError(result.error || "Incorrect password.");
        return;
      }
      const expiresAt = Date.now() + (result.expiresInMs || 5 * 60 * 1000);
      setTitleAuthToken(result.token);
      sessionStorage.setItem("titleEditToken", result.token);
      sessionStorage.setItem("titleEditTokenExpiry", String(expiresAt));
      setShowTitleAuthModal(false);
      setTitleAuthPassword("");
      await doSaveTitle(result.token);
    } catch (err) {
      setTitleAuthError("Unable to verify password. Please try again.");
    } finally {
      setTitleAuthVerifying(false);
    }
  };

  const vocabularyList = unit?.vocabulary || [];

  const filteredList = vocabularyList.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.spanish || "").toLowerCase().includes(term) ||
      (item.english || "").toLowerCase().includes(term) ||
      (item.chinese || "").toLowerCase().includes(term)
    );
  });

  const formattedDate = unit?.createdAt
    ? new Date(unit.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
    : "Curated Lesson";

  // ── Undo Item Deletion (Restore word from pending state) ─────────────────────
  const cancelItemDeletion = useCallback((spanish) => {
    pendingDeletesRef.current.delete(spanish);
    setPendingItemDeletes(new Set(pendingDeletesRef.current));
  }, []);

  // ── Trigger delete request ─────────────────────────────────────────────────
  const requestDelete = (action) => {
    setPendingAction(action);
    const cachedToken = getDeleteSessionToken();
    if (cachedToken) {
      handleAuthorizedAction(action, cachedToken);
    } else {
      setPwdInput("");
      setPwdError("");
      setShowPwdEye(false);
      setShowPwdModal(true);
    }
  };

  const handleAuthorizedAction = (action, token) => {
    if (!action) return;
    sessionTokenRef.current = token;
    if (action.type === "unit") {
      executeUnitDelete(token);
    } else if (action.type === "item") {
      pendingDeletesRef.current.add(action.item.spanish);
      setPendingItemDeletes(new Set(pendingDeletesRef.current));
    }
    setPendingAction(null);
  };

  const handleModalSubmit = async () => {
    if (pwdInput.length !== 6) return;
    setPwdVerifying(true);
    const result = await createVocabDeleteSession(pwdInput);
    setPwdVerifying(false);

    if (!result.valid) {
      setPwdError("Incorrect password. Please try again.");
      return;
    }

    cacheDeleteSession(result.token, result.expiresInMs);
    sessionTokenRef.current = result.token;
    setShowPwdModal(false);
    handleAuthorizedAction(pendingAction, result.token);
  };

  const executeUnitDelete = async (token) => {
    setDeleting(true);
    setIsDriftingAway(true); // 1. 瞬间触发 CSS 飞走动画

    try {
      // 2. 让后端 API 请求与前端动画等待并行 (Promise.all)
      // 假设CSS 动画时长已经缩短到了 400ms
      const animationTimer = new Promise((resolve) => setTimeout(resolve, 400));

      await Promise.all([
        deleteVocabularyUnit(id, token), // 发送 API 请求
        animationTimer                  // 确保动画至少播放满 400ms
      ]);

      // 3. 动画播完且 API 请求也完成了，无缝直接跳转
      navigate("/study/spanish");
    } catch (err) {
      // 如果 API 失败，取消飘走状态并恢复 UI
      setIsDriftingAway(false);
      setDeleting(false);
      setPendingAction(null);
      alert(err.message || "Failed to delete vocabulary unit.");
    }
  };

  if (loading) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.emptyState}>
          <Loader2 className="animate-spin inline-block mr-2" size={20} />
          Loading unit vocabulary…
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.topNav}>
          <Link to="/study/spanish" className={styles.backBtn}>
            <ArrowLeft size={14} />
            <span>Back to Spanish Dashboard</span>
          </Link>
        </div>
        <div className={styles.emptyState}>
          <BookOpen size={36} className="mx-auto mb-2 opacity-50" />
          <p>Vocabulary unit not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.vocabPage} ${isDriftingAway ? styles.pageFlyAway : ""}`}>
      {/* ── Top Navigation ── */}
      <div className={styles.topNav}>
        <button
          type="button"
          onClick={() => navigate("/study/spanish")}
          className={styles.backBtn}
        >
          <ArrowLeft size={14} />
          <span>Back to Spanish Dashboard</span>
        </button>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => requestDelete({ type: "unit" })}
            disabled={deleting}
            className={styles.deleteUnitBtn}
            title="Delete this entire vocabulary unit"
          >
            <Trash2 size={13} />
            <span>{deleting && pendingAction?.type === "unit" ? "Deleting…" : "Delete Unit"}</span>
          </button>

          <button
            type="button"
            onClick={handleTakeExamClick}
            className={styles.examLinkBtn}
            title={latestRecord ? "View your latest exam result" : "Take this exam"}
          >
            <FileText size={13} />
            <span>{latestRecord ? "View Result" : "Take Exam"}</span>
          </button>
        </div>
      </div>

      {/* ── Unit Header Card ── */}
      <div className={styles.headerCard}>
        <div className={styles.headerTitleRow}>
          <div className={styles.unitTitleWrap}>
            {isEditingTitle ? (
              <form
                className={styles.unitTitleEditForm}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveTitle();
                }}
              >
                <input
                  ref={titleInputRef}
                  type="text"
                  className={styles.unitTitleInput}
                  value={editedTitle}
                  onChange={(e) => {
                    setEditedTitle(e.target.value);
                    if (titleSaveError) setTitleSaveError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      handleCancelEditTitle();
                    }
                  }}
                  disabled={isSavingTitle}
                  maxLength={120}
                />
                <div className={styles.unitTitleEditActions}>
                  <button
                    type="submit"
                    className={styles.titleSaveBtn}
                    disabled={isSavingTitle || !editedTitle.trim()}
                    title="Save title"
                  >
                    {isSavingTitle ? (
                      "Saving..."
                    ) : (
                      <>
                        <Check size={13} strokeWidth={2.5} />
                        <span>Save</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className={styles.titleCancelBtn}
                    onClick={handleCancelEditTitle}
                    disabled={isSavingTitle}
                    title="Cancel"
                  >
                    <X size={13} strokeWidth={2.5} />
                    <span>Cancel</span>
                  </button>
                </div>
              </form>
            ) : (
              <div
                className={styles.unitTitleDisplayWrap}
                onClick={handleStartEditTitle}
                title="Click to edit title"
              >
                <h1 className={styles.unitTitle}>{cleanTitle(unit.title)}</h1>
                <button
                  type="button"
                  className={styles.unitTitleEditBtn}
                  aria-label="Edit unit title"
                  title="Edit unit title"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEditTitle();
                  }}
                >
                  <Edit3 size={15} />
                </button>
              </div>
            )}
            {titleSaveError && <p className={styles.titleSaveError}>{titleSaveError}</p>}
          </div>

          <div className={styles.unitBadges}>
            <span className={styles.countBadge}>
              {vocabularyList.length} words / phrases
            </span>
          </div>
        </div>

        <div className={styles.dateText}>
          <Calendar size={13} />
          <span>Created: {formattedDate}</span>
        </div>
      </div>

      {/* ── Search Bar ── */}
      {vocabularyList.length > 5 && (
        <div className={styles.searchBar}>
          <Search size={15} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search in this vocabulary unit (Spanish, English, Chinese)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {/* ── Vocabulary Table ── */}
      <div className={styles.tableCard}>
        {filteredList.length === 0 ? (
          <div className={styles.emptyState}>
            No vocabulary items match your search.
          </div>
        ) : (
          <table className={styles.vocabTable}>
            <thead>
              <tr>
                <th className={styles.colIndex}>No.</th>
                <th className={styles.colSpanish}>Word / Phrase</th>
                <th className={styles.colEnglish}>English Translation</th>
                <th className={styles.colChinese}>中文翻译</th>
                <th className={styles.colAction}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((item, idx) => {
                const { base } = extractBaseWord(item.spanish);
                const pos = (item.type || "").toLowerCase();
                const isVerb = pos.includes("verb");
                const isPhrase = pos.includes("phrase");
                const isNoun = pos.includes("noun") || pos.includes("sustantivo");
                const isAdj = pos.includes("adj");
                const originalIdx = vocabularyList.indexOf(item);
                const isPendingDelete = pendingItemDeletes.has(item.spanish);
                const itemKey = `${item.spanish}-${idx}`;
                const isExpanded = Boolean(expandedKeys[itemKey]);

                return (
                  <React.Fragment key={itemKey}>
                    <tr className={isPendingDelete ? styles.rowPendingDelete : ""}>
                      {/* 序号 */}
                      <td className={styles.colIndex}>#{idx + 1}</td>

                      {/* 单词 / 词组 (原形 + 点击弹出变形) */}
                      <td className={styles.colSpanish}>
                        <div className={styles.spanishWord}>
                          {/* Clickable base word */}
                          <button
                            type="button"
                            onClick={() => toggleExpand(itemKey)}
                            className={styles.wordClickable}
                            title="Click to view conjugations & inflections"
                          >
                            <span>{base}</span>
                          </button>

                          {/* Part of speech tag */}
                          {pos && (
                            <span
                              className={`${styles.posTag} ${isVerb
                                ? styles.posTagVerb
                                : isPhrase
                                  ? styles.posTagPhrase
                                  : ""
                                }`}
                            >
                              {pos}
                            </span>
                          )}

                          {/* Expand Inflection Button */}
                          <button
                            type="button"
                            onClick={() => toggleExpand(itemKey)}
                            className={`${styles.inflectionBadge} ${isExpanded ? styles.inflectionBadgeActive : ""
                              }`}
                            title="Toggle inflections"
                          >
                            <span>
                              {isVerb
                                ? "时态变形"
                                : isNoun
                                  ? "单复数"
                                  : isAdj
                                    ? "性数变形"
                                    : "词形"}
                            </span>
                            <ChevronDown
                              size={12}
                              className={`${styles.chevronIcon} ${isExpanded ? styles.chevronIconRotated : ""
                                }`}
                            />
                          </button>

                          {/* Audio pronunciation */}
                          {!isPendingDelete && (
                            <button
                              type="button"
                              onClick={() => speak(item.spanish)}
                              className={styles.soundBtn}
                              title="Listen pronunciation"
                            >
                              <Volume2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* 英文翻译 */}
                      <td className={styles.colEnglish}>
                        {item.english || "—"}
                      </td>

                      {/* 中文翻译 */}
                      <td className={styles.colChinese}>
                        {item.chinese || "—"}
                      </td>

                      {/* 删除 / 反旋转撤销 按钮 */}
                      <td className={styles.colAction}>
                        {isPendingDelete ? (
                          <button
                            type="button"
                            onClick={() => cancelItemDeletion(item.spanish)}
                            className={styles.rowUndoBtn}
                            title="待删除状态：点击撤销删除 (Word marked for deletion. Click to cancel)"
                          >
                            <RotateCcw size={12} className={styles.undoIcon} />
                            {/* <span>待删除</span> */}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              requestDelete({
                                type: "item",
                                item,
                                index: originalIdx !== -1 ? originalIdx : idx,
                              })
                            }
                            className={styles.rowDeleteBtn}
                            title="Delete this word from unit"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* ── Dynamic Inflections Drawer (Beneath the Word) ── */}
                    {isExpanded && (
                      <tr className={styles.inflectionDrawerRow}>
                        <td colSpan={5}>
                          <InflectionDrawer item={item} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Password Modal ── */}
      {showPwdModal && (
        <div className={styles.modalOverlay} onClick={() => setShowPwdModal(false)}>
          <div
            className={`${styles.modalCard} ${pwdError ? styles.pinShakeAnim : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <ShieldAlert size={18} className={styles.modalIcon} />
              <span>Enter Delete Password</span>
              <button
                type="button"
                onClick={() => setShowPwdModal(false)}
                className={styles.modalCloseBtn}
              >
                <X size={15} />
              </button>
            </div>

            <p className={styles.modalDesc}>
              Enter the 6-digit PIN to authorize deletion. Password remains active for <strong>5 minutes</strong>.
            </p>

            {/* 6-Digit Interactive PIN Container with Eye Button */}
            <div className={styles.pinInputWrapper}>
              {/* 6 Animated PIN Boxes Area */}
              <div
                className={styles.pinBoxesRow}
                onClick={() => pwdRef.current?.focus()}
              >
                {/* Hidden native input with full keyboard, paste & navigation support */}
                <input
                  ref={pwdRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pwdInput}
                  onChange={(e) => {
                    setPwdInput(e.target.value.replace(/\D/g, ""));
                    setPwdError("");
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      pwdInput.length === 6 &&
                      !pwdVerifying
                    ) {
                      handleModalSubmit();
                    }
                  }}
                  className={styles.hiddenInputOverlay}
                  autoFocus
                />

                {Array.from({ length: 6 }).map((_, idx) => {
                  const isFilled = idx < pwdInput.length;
                  const isActive = idx === pwdInput.length;
                  const digit = pwdInput[idx];

                  return (
                    <div
                      key={idx}
                      className={`${styles.pinBox} ${isFilled ? styles.pinBoxFilled : ""
                        } ${isActive ? styles.pinBoxActive : ""} ${pwdError ? styles.pinBoxError : ""
                        }`}
                    >
                      {isFilled ? (
                        showPwdEye ? (
                          <span
                            key={`digit-${idx}-${digit}`}
                            className={styles.pinDigit}
                          >
                            {digit}
                          </span>
                        ) : (
                          <span
                            key={`bullet-${idx}`}
                            className={styles.pinBullet}
                          />
                        )
                      ) : isActive ? (
                        <span className={styles.pinBlinkingCursor} />
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Eye Visibility Toggle Button (borderless) */}
              <button
                type="button"
                className={styles.pinEyeBtn}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPwdEye((prev) => !prev);
                }}
                title={showPwdEye ? "Hide password" : "Show password"}
                aria-label={showPwdEye ? "Hide password" : "Show password"}
              >
                {showPwdEye ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {pwdError && (
              <p className={styles.modalError}>{pwdError}</p>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowPwdModal(false)}
                className={styles.modalCancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalSubmit}
                disabled={pwdInput.length !== 6 || pwdVerifying}
                className={styles.modalConfirmBtn}
              >
                {pwdVerifying ? "Verifying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Exam Access Password Modal ── */}
      {showExamPwdModal && (
        <div className={styles.modalOverlay} onClick={() => setShowExamPwdModal(false)}>
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <FileText size={18} className={styles.modalIcon} />
              <span>{latestRecord ? "View Result — Password Required" : "Take Exam — Password Required"}</span>
              <button
                type="button"
                onClick={() => setShowExamPwdModal(false)}
                className={styles.modalCloseBtn}
              >
                <X size={15} />
              </button>
            </div>

            <p className={styles.modalDesc}>
              Enter the 6-digit exam password to continue.
            </p>

            <input
              ref={examPwdRef}
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={examPwdInput}
              onChange={(e) => {
                setExamPwdInput(e.target.value.replace(/\D/g, ""));
                setExamPwdError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && examPwdInput.length === 6 && handleExamPwdSubmit()}
              placeholder="6-digit PIN"
              className={styles.modalPwdInput}
            />

            {examPwdError && (
              <p className={styles.modalError}>{examPwdError}</p>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowExamPwdModal(false)}
                className={styles.modalCancelBtn}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExamPwdSubmit}
                disabled={examPwdInput.length !== 6 || examPwdVerifying}
                className={styles.modalConfirmBtn}
              >
                {examPwdVerifying ? "Verifying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Title Auth Password Modal ── */}
      {showTitleAuthModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowTitleAuthModal(false);
            setTitleAuthPassword("");
            setTitleAuthError("");
          }}
        >
          <div
            className={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="title-auth-modal-title"
          >
            <div className={styles.modalHeader}>
              <Lock size={18} className={styles.modalIcon} />
              <span id="title-auth-modal-title">Edit Unit Title</span>
              <button
                type="button"
                onClick={() => {
                  setShowTitleAuthModal(false);
                  setTitleAuthPassword("");
                  setTitleAuthError("");
                }}
                className={styles.modalCloseBtn}
                disabled={titleAuthVerifying}
              >
                <X size={15} />
              </button>
            </div>

            <p className={styles.modalDesc}>
              Enter the exam password to save changes to the title. The session stays active for <strong>5 minutes</strong>.
            </p>

            {/* 6-Digit Interactive PIN Container with Eye Button */}
            <div className={styles.pinInputWrapper}>
              {/* 6 Animated PIN Boxes Area */}
              <div
                className={styles.pinBoxesRow}
                onClick={() => titleAuthInputRef.current?.focus()}
              >
                {/* Hidden native input with full keyboard, paste & navigation support */}
                <input
                  ref={titleAuthInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={titleAuthPassword}
                  onChange={(e) => {
                    setTitleAuthPassword(e.target.value.replace(/\D/g, ""));
                    setTitleAuthError("");
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      titleAuthPassword.length === 6 &&
                      !titleAuthVerifying
                    ) {
                      handleTitleAuthSubmit();
                    }
                    if (e.key === "Escape") {
                      setShowTitleAuthModal(false);
                      setTitleAuthPassword("");
                      setTitleAuthError("");
                    }
                  }}
                  className={styles.hiddenInputOverlay}
                  autoFocus
                />

                {Array.from({ length: 6 }).map((_, idx) => {
                  const isFilled = idx < titleAuthPassword.length;
                  const isActive = idx === titleAuthPassword.length;
                  const digit = titleAuthPassword[idx];

                  return (
                    <div
                      key={idx}
                      className={`${styles.pinBox} ${isFilled ? styles.pinBoxFilled : ""
                        } ${isActive ? styles.pinBoxActive : ""} ${titleAuthError ? styles.pinBoxError : ""
                        }`}
                    >
                      {isFilled ? (
                        showTitleAuthEye ? (
                          <span
                            key={`digit-${idx}-${digit}`}
                            className={styles.pinDigit}
                          >
                            {digit}
                          </span>
                        ) : (
                          <span
                            key={`bullet-${idx}`}
                            className={styles.pinBullet}
                          />
                        )
                      ) : isActive ? (
                        <span className={styles.pinBlinkingCursor} />
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {/* Eye Visibility Toggle Button (borderless) */}
              <button
                type="button"
                className={styles.pinEyeBtn}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowTitleAuthEye((prev) => !prev);
                }}
                title={showTitleAuthEye ? "Hide password" : "Show password"}
                aria-label={showTitleAuthEye ? "Hide password" : "Show password"}
              >
                {showTitleAuthEye ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {titleAuthError && (
              <p className={styles.modalError}>{titleAuthError}</p>
            )}

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => {
                  setShowTitleAuthModal(false);
                  setTitleAuthPassword("");
                  setTitleAuthError("");
                }}
                className={styles.modalCancelBtn}
                disabled={titleAuthVerifying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTitleAuthSubmit}
                disabled={titleAuthPassword.length !== 6 || titleAuthVerifying}
                className={styles.modalConfirmBtn}
              >
                {titleAuthVerifying ? "Verifying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
